import React, {useEffect, useState} from "react";
import {
  Box, ButtonBase, IconButton, InputBase, MenuItem, Stack, TextField, Tooltip, Typography, useMediaQuery, useTheme
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Call02Icon, Cancel01Icon} from "@hugeicons/core-free-icons";
import {useWebrtc} from "../../context/WebrtcContext";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";
import {subProjectLabel} from "../../utils/subProjects";

// a phone number: optional leading +, then digits (spaces, dashes, dots and brackets are ignored)
const normalize = (value) => String(value || "").trim().replace(/[\s\-().]/g, "");
const isPhoneNumber = (value) => /^\+?\d{3,15}$/.test(normalize(value));

const outboundOf = (subProject) => subProject?.coreConfiguration?.outbound_call || "";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "0"];

// Dial pad of the phone panel: this browser rings first (answered automatically), then the number is dialed from the
// chosen sub project's outbound caller ID, and the two are bridged once it answers.
export const PhoneDialTab = () => {
  const {project, subProject} = useRegistryProject();
  const {status, call, callCustomer} = useWebrtc();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const subProjects = project?.subProjects || [];
  const [number, setNumber] = useState("");
  const [subProjectId, setSubProjectId] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setSubProjectId(String(subProject?.id || subProjects[0]?.id || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subProject?.id]);

  const chosen = subProjects.find(item => String(item.id) === String(subProjectId)) || subProject;
  const valid = isPhoneNumber(number);
  const connected = status === "registered";
  const press = (key) => {
    setTouched(false);
    setNumber(current => current + key);
  };

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !connected || call) return;
    callCustomer({projectId: project?.id, subProjectId: subProjectId || undefined, phoneNumber: normalize(number)});
    setNumber("");
    setTouched(false);
  };

  return (
    <Stack component={"form"} onSubmit={submit} sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2, py: 1.5}}>
      <Stack direction={"row"} alignItems={"center"} gap={0.5} sx={{pl: 4.5}}>
        <InputBase
          // on phones the keyboard would cover the dial pad
          autoFocus={!isMobile} fullWidth placeholder={"+389 70 123 456"} value={number}
          onChange={(e) => setNumber(e.target.value)}
          inputProps={{inputMode: "tel", autoComplete: "tel", "aria-label": "Phone number", style: {textAlign: "center"}}}
          sx={{fontSize: 24, fontWeight: 600, color: "#23264D"}}
        />
        <Tooltip title={"Delete the last digit"}>
          <span>
            <IconButton
              aria-label={"Delete the last digit"} disabled={!number} sx={{color: "#8A8AA0"}}
              onClick={() => setNumber(current => current.slice(0, -1))}
            >
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Typography sx={{minHeight: 18, fontSize: 12, textAlign: "center", color: "#C73A3A"}}>
        {touched && !valid ? "Enter a phone number, digits with an optional leading +" : ""}
      </Typography>

      <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", justifyItems: "center", rowGap: 1, my: 1}}>
        {KEYS.map(key => (
          <ButtonBase
            key={key} type={"button"} onClick={() => press(key)} aria-label={`Dial ${key}`}
            sx={{
              width: 60, height: 60, borderRadius: "50%", fontSize: 22, fontWeight: 600, color: "#23264D", bgcolor: "#F4F4FA",
              "&:hover": {bgcolor: "#ECECF3"},
            }}
          >
            {key}
          </ButtonBase>
        ))}
        <Tooltip title={call ? "You are already on a call" : "Call"}>
          <span>
            <IconButton
              type={"submit"} aria-label={"Call"} disabled={!connected || Boolean(call)}
              sx={{
                width: 60, height: 60, color: "#fff", background: "linear-gradient(145deg, #6FE39F, #2FC275)", boxShadow: "0 8px 24px rgba(47, 194, 117, 0.4)",
                "&:hover": {background: "linear-gradient(145deg, #62D893, #27B56A)"},
                "&.Mui-disabled": {color: "#fff", background: "#C9CAD8", boxShadow: "none"},
              }}
            >
              <HugeiconsIcon icon={Call02Icon} size={24}/>
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {subProjects.length > 1 && (
        <TextField
          select fullWidth size={"small"} label={"Call from"} value={subProjectId}
          onChange={(e) => setSubProjectId(e.target.value)}
          sx={{mt: 1, "& .MuiOutlinedInput-root": {borderRadius: "10px", fontSize: 14}}}
        >
          {subProjects.map(item => (
            <MenuItem key={item.id} value={String(item.id)} sx={{fontSize: 14}}>{subProjectLabel(item)}</MenuItem>
          ))}
        </TextField>
      )}
      <Typography sx={{fontSize: 12, mt: 1, textAlign: "center", color: outboundOf(chosen) ? "#8A8AA0" : "#C73A3A"}}>
        {outboundOf(chosen)
          ? `Caller ID: ${outboundOf(chosen)}`
          : "No outbound caller ID is set for this sub project (Settings → Outbound Caller ID)."}
      </Typography>
      {!connected && (
        <Typography sx={{fontSize: 12, mt: 0.5, textAlign: "center", color: "#C73A3A"}}>Your browser phone is not connected.</Typography>
      )}
    </Stack>
  );
};
